import React, { useEffect, useState } from 'react';

// Enums
import
{
    PenlyPlayModeEnum,
    PenlyPlayPensionAgeChoicesEnum,
    PenlyPlayPensionAgeGainChoicesEnum,
    PenlyPlayPensionDepositChoicesEnum,
    PenlyPlayPensionDepositGainChoicesEnum,
    PenlyPlayPensionPaymentChoicesEnum,
    PenlyPlayPensionPaymentGainChoicesEnum,
    PenlyPlayPensionChoicesEnum,
} from 'utils/enums';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { resetPenlyPlayState, updatePenlyPlayState } from 'store/actions/penlyplay';

// Next
import { useRouter } from 'next/router';
import getConfig from 'next/config';

// Utils
import { renderKr, round } from 'utils/renderingService';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { Routes } from 'utils/routes';
import {
    getCurrentCalculation,
    getEstimatedPensionAge,
    getPaymentCalculation,
    getDepositCalculation,
    getAgeCalculation,
    getPenlyPlayPensionGainStepTitle,
    getPenlyPlayPensionResultStepTitle,
    isAgeStateValid,
    isSalaryBeforeTaxStateValid,
    isPensionSavedToDateStateValid,
    isPensionDepositPerMonthInPercentValid,
} from 'utils/PenlyPlay/penlyPlayPensionHelpers';

// Form fields
import TextField from '@material-ui/core/TextField';
import { CurrencyFormat, AgeFormat }from 'utils/inputFormatting';

// Components
import
{
    Slider,
    Spacer,
    Button,
    PenlyPlayDevelopmentGraph,
}
    from 'components';

/* Auth */
import useAuthentication from 'utils/useAuthentication';

let logCounter = 0;

const PenlyPlayPension = ({ ...props }) => {

    const router = useRouter();
    const { trackEvent } = useMatomo();
    const { authenticated, login } = useAuthentication();
    const { publicRuntimeConfig } = getConfig();
    const [initialized, setInitialized] = useState(false);

    /* #region Debug */
    const log = (text) => {

        if (!logCounter) {
            logCounter = 1;
        } else {
            logCounter += 1;
        }

        if (publicRuntimeConfig.env === 'dev') {
            // eslint-disable-next-line no-console
            console.log(`#${logCounter} ${text}`);
        }
    };
    /* #endregion */

    /* #region PenlyPlay pension config values */

    const minPensionAge = 55;
    const maxPensionAge = 85;
    const minDepositPercent = 1;
    const maxDepositPercent = 50;

    /* #endregion */

    /* #region Penly Play state values */

    const userPlayState =
    {
        basic:
        {
            age: 44,
            salaryBeforeTax: 34500,
        },
        pension:
        {
            pensionSavedToDate: 1234567,
        },
    };

    const initialPlayState =
    {
        pension:
        {
            depositPerMonthInPercentCurrent: 10,
        },
    };

    const getInitialPenlyPlayState = (initialValues) => {

        const defaultPenlyPlayState = {
            initialized: false,

            // Non public play state is used within MyPenly where initial values are from the users profile.
            mode: initialValues?.mode ?? PenlyPlayModeEnum.Public,

            basic:
            {
                // The persons age
                age: initialValues?.basic?.age ?? null,

                // The persons salary before tax per month
                salaryBeforeTax: initialValues?.basic?.salaryBeforeTax ?? null,
            },

            pension:
            {
                /* ****** Non calculated user input ****** */
                // The persons total pension saved to date
                pensionSavedToDate: initialValues?.pension?.pensionSavedToDate ?? null,

                // Investment level. (Not currently used as input)
                investmentRiskLevel: initialValues?.pension?.investmentRiskLevel ?? null,

                /* ****** Initial calculated values ****** */
                // Estimated pensionage calculated from the users age from star.dk
                pensionAgeEstimated: initialValues?.pension?.pensionAgeEstimated ?? null,

                /* ****** Current values calculated from user input ****** */
                pensionAgeCurrent: initialValues?.pension?.pensionAgeCurrent ?? null,
                depositPerMonthCurrent: initialValues?.pension?.depositPerMonthCurrent ?? null,
                depositPerMonthInPercentCurrent: initialValues?.pension?.depositPerMonthInPercentCurrent ?? null,
                savingsAtPensionCurrent: initialValues?.pension?.savingsAtPensionCurrent ?? null,
                paymentPerMonthCurrent: initialValues?.pension?.paymentPerMonthCurrent ?? null,
                pensionDevelopmentCurrent: initialValues?.pension?.pensionDevelopmentCurrent ?? null,

                /* ****** Calculated values ****** */
                pensionAgeCalculated: null,
                depositPerMonthCalculated: null,
                depositPerMonthInPercentCalculated: null,
                paymentPerMonthCalculated: null,
                savingsAtPensionCalculated: null,
                pensionDevelopmentCalculated: null,

                /* ****** Result values used between calculation ****** */
                calculationValues: null,

                /* ****** Play choices****** */
                // What does the person want to change? Age, Deposit or Payment
                pensionChoice: initialValues?.pension?.pensionChoice ?? null,

                // Does the person want to increase or decrease his pension age?
                pensionAgeChoice: initialValues?.pension?.pensionAgeChoice ?? PenlyPlayPensionAgeChoicesEnum.Unknown,
                // How will the person obtain the change? Lower/higher deposit or payment?
                pensionAgeGainChoice: initialValues?.pension?.pensionAgeGainChoice ?? PenlyPlayPensionAgeGainChoicesEnum.Unknown,

                // Does the person want to increase or decrease his deposits?
                pensionDepositChoice: initialValues?.pension?.pensionDepositChoice ?? PenlyPlayPensionDepositChoicesEnum.Unknown,
                // What does the person want to gain from the change? Lower/higher age or payment?
                pensionDepositGainChoice: initialValues?.pension?.pensionDepositGainChoice ?? PenlyPlayPensionDepositGainChoicesEnum.Unknown,

                // Does the person want to increase or decrease his payments?
                pensionPaymentChoice: initialValues?.pension?.pensionPaymentChoice ?? PenlyPlayPensionPaymentChoicesEnum.Unknown,
                // What does the person want to gain from the change? Lower/higher age or deposit?
                pensionPaymentGainChoice: initialValues?.pension?.pensionPaymentGainChoice ?? PenlyPlayPensionPaymentGainChoicesEnum.Unknown,
            },
        };

        return defaultPenlyPlayState;
    };

    /* #endregion */

    /* #region Redux and state  */
    const dispatch = useDispatch();
    const penlyPlayState = useSelector(state => state.penlyplay);
    /* #endregion */

    /* #region Local state handling */
    const [age, setAge] = useState(null);
    const [salaryBeforeTax, setSalaryBeforeTax] = useState(null);
    const [pensionSavedToDate, setPensionSavedToDate] = useState(null);
    const [depositPerMonthInPercentCurrent, setDepositPerMonthInPercentCurrent] = useState(null);
    const [baseValueChanged, setBaseValueChanged] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [doFlash, setDoFlash] = useState(false);
    const [showCalculationInfo, setShowCalculationInfo] = useState(false);

    const handleInputBlur = () => {
        log('handleInputBlur()');
        setBaseValueChanged(true);

        // Update from local state
        const updatedState = { ...penlyPlayState };
        updatedState.basic.age = parseInt(age, 0) ?? null;
        updatedState.basic.salaryBeforeTax = parseFloat(salaryBeforeTax) ?? null;
        updatedState.pension.pensionSavedToDate = parseFloat(pensionSavedToDate) ?? null;
        dispatch(updatePenlyPlayState(updatedState));

        trackEvent({ category: 'PenlyPlayPension', action: 'BasicInputChanged' });
    };

    const handleDepositPerMonthInPercentCurrentInputBlur = () => {
        log('handleDepositPerMonthInPercentCurrentInputBlur()');
        if (penlyPlayState.pension.depositPerMonthInPercentCurrent !== depositPerMonthInPercentCurrent) {
            setBaseValueChanged(true);
            const updatedState = { ...penlyPlayState };
            updatedState.pension.depositPerMonthInPercentCurrent = parseFloat(depositPerMonthInPercentCurrent);
            dispatch(updatePenlyPlayState(updatedState));

            trackEvent({ category: 'PenlyPlayPension', action: 'BasicInputChanged' });
        }
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            log('handleKeyDown()');
            handleInputBlur(event);
        }
    };

    const depositPerMonthMarks = [
        {
            value: minDepositPercent,
            label: `${minDepositPercent}%`,
        },
        {
            value: maxDepositPercent,
            label: `${maxDepositPercent}%`,
        },
    ];

    function percentText(value) {
        return `${value}%`;
    }

    /* #endregion */

    /* #region Input mapped state handling */

    // React to age state change
    useEffect(() => {
        if (penlyPlayState) {
            log('useEffect() - age state changed');

            const updatedState = { ...penlyPlayState };
            updatedState.pension.pensionAgeCurrent = getEstimatedPensionAge(penlyPlayState.basic.age);
            log(`updatedState.pension.pensionAgeCurrent: ${  updatedState.pension.pensionAgeCurrent}`);

            dispatch(updatePenlyPlayState(updatedState));
            setAge(penlyPlayState.basic.age);

            if (isAgeStateValid(updatedState)) {
                ;(async () => {
                    await updateCurrentCalculation();
                })();
            }
        }
    }, [penlyPlayState?.basic.age]);

    // React to salaryBeforeTax state change
    useEffect(() => {
        if (penlyPlayState) {
            log('useEffect() - salaryBeforeTax state changed');
            const updatedState = { ...penlyPlayState };
            updatedState.pension.depositPerMonthCurrent = round((penlyPlayState.basic.salaryBeforeTax * depositPerMonthInPercentCurrent) / 100);
            dispatch(updatePenlyPlayState(updatedState));
            setSalaryBeforeTax(penlyPlayState.basic.salaryBeforeTax);
            if (isSalaryBeforeTaxStateValid(updatedState)) {
                ;(async () => {
                    await updateCurrentCalculation();
                })();
            }
        }
    }, [penlyPlayState?.basic.salaryBeforeTax]);

    // React to pensionSavedToDate state change
    useEffect(() => {
        if (penlyPlayState) {
            log('useEffect() - pensionSavedToDate state changed');
            setPensionSavedToDate(penlyPlayState.pension.pensionSavedToDate);
            if (isPensionSavedToDateStateValid(penlyPlayState)) {
                ;(async () => {
                    await updateCurrentCalculation();
                })();
            }
        }
    }, [penlyPlayState?.pension.pensionSavedToDate]);

    // React to depositPerMonthInPercentCurrent state change
    useEffect(() => {
        if (penlyPlayState) {
            log('useEffect() - depositPerMonthInPercentCurrent state changed');
            const updatedState = { ...penlyPlayState };
            updatedState.pension.depositPerMonthCurrent = parseFloat((updatedState.basic.salaryBeforeTax * updatedState.pension.depositPerMonthInPercentCurrent) / 100);
            dispatch(updatePenlyPlayState(updatedState));
            setDepositPerMonthInPercentCurrent(penlyPlayState.pension.depositPerMonthInPercentCurrent);
            if (isPensionDepositPerMonthInPercentValid(updatedState)) {
                ;(async () => {
                    await updateCurrentCalculation();
                })();
            }
        }
    }, [penlyPlayState?.pension.depositPerMonthInPercentCurrent]);

    /* #endregion */

    /* #region Calculated state handling */

    // React to calculated state
    useEffect(() => {
        if (penlyPlayState) {
            const flashElements = document.querySelectorAll('.valueFlash');

            if (flashElements && doFlash) {
                flashElements.forEach((element) => {
                    element.classList.remove('flashContainer');
                    setTimeout(() => {
                        element.classList.add('flashContainer');
                        setDoFlash(false);
                    }, 1);
                });
            }
        }
    }, [doFlash]);

    /* #endregion */

    /* #region Pension choice handling */

    const onPensionChoiceClick = (event, choice) => {
        log(`PensionChoiceClick() - ${choice}`);
        const updatedState = { ...penlyPlayState };
        updatedState.pension.pensionChoice = choice;
        updatedState.pension.pensionAgeChoice = PenlyPlayPensionAgeChoicesEnum.Unknown;
        updatedState.pension.pensionDepositChoice = PenlyPlayPensionDepositChoicesEnum.Unknown;
        updatedState.pension.pensionPaymentChoice = PenlyPlayPensionPaymentChoicesEnum.Unknown;
        updatedState.pension.pensionAgeGainChoice = PenlyPlayPensionAgeGainChoicesEnum.Unknown;
        updatedState.pension.pensionDepositGainChoice = PenlyPlayPensionDepositGainChoicesEnum.Unknown;
        updatedState.pension.pensionPaymentGainChoice = PenlyPlayPensionPaymentGainChoicesEnum.Unknown;

        resetCalculatedValues(updatedState);
        dispatch(updatePenlyPlayState(updatedState));

        setPensionChoiceAgePensionAge(penlyPlayState.pension.pensionAgeCurrent);
        setPensionChoiceDepositDepositPercent(penlyPlayState.pension.depositPerMonthInPercentCurrent);
        setPensionChoicePaymentPaymentAmount(penlyPlayState.pension.paymentPerMonthCurrent);

        trackEvent({ category: 'PenlyPlayPension', action: 'PensionChoiceClicked' });

        event.preventDefault();
    };

    /* #endregion */

    /* #region Pension choice handling: Age */

    const [pensionChoiceAgePensionAge, setPensionChoiceAgePensionAge] = useState(null);

    function pensionChoiceAgePensionAgeText(value) {
        if (value) {
            return `${value} år`;
        }
    }

    const getPensionChoiceAgePensionAgeMarks = () => {
        let pensionAgeEstimated = penlyPlayState.pension.pensionAgeCurrent > maxPensionAge ? maxPensionAge : penlyPlayState.pension.pensionAgeCurrent;
        pensionAgeEstimated = pensionAgeEstimated < minPensionAge ? minPensionAge : pensionAgeEstimated;

        return [
            {
                value: minPensionAge,
                label: `${minPensionAge} år`,
            },
            {
                value: pensionAgeEstimated,
                label: `${pensionAgeEstimated} år (estimeret)`,
            },
            {
                value: maxPensionAge,
                label: `${maxPensionAge} år`,
            }];
    };

    const pensionChoiceAgeHandlePensionAgeChange = (event, value) => {
        setPensionChoiceAgePensionAge(value);
    };

    const pensionChoiceAgeHandlePensionAgeChangeBlur = async () => {
        const updatedState = { ...penlyPlayState };
        let runCalculation = false;

        // Reset other choice values
        updatedState.pension.pensionDepositChoice = PenlyPlayPensionDepositChoicesEnum.Unknown;
        updatedState.pension.pensionPaymentChoice = PenlyPlayPensionPaymentChoicesEnum.Unknown;

        if (pensionChoiceAgePensionAge > updatedState.pension.pensionAgeCurrent) {
            if (updatedState.pension.pensionAgeChoice === PenlyPlayPensionAgeChoicesEnum.LowerAge) {
                updatedState.pension.pensionAgeGainChoice = PenlyPlayPensionAgeGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionAgeChoice = PenlyPlayPensionAgeChoicesEnum.HigherAge;
        } else {
            if (updatedState.pension.pensionAgeChoice === PenlyPlayPensionAgeChoicesEnum.HigherAge) {
                updatedState.pension.pensionAgeGainChoice = PenlyPlayPensionAgeGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionAgeChoice = PenlyPlayPensionAgeChoicesEnum.LowerAge;
        }

        updatedState.pension.pensionAgeCalculated = pensionChoiceAgePensionAge;
        dispatch(updatePenlyPlayState(updatedState));

        if (runCalculation) {
            runUpdateCalculationBasedOnGainChoice(updatedState);
        }

        const titleElement = document.querySelector('#penlyPlayPensionGainTitle');

        if (titleElement) {
            const titleText = getPenlyPlayPensionGainStepTitle(updatedState);
            titleElement.innerHTML = titleText;
        }

        trackEvent({ category: 'PenlyPlayPension', action: 'PensionAgeChanged' });
    };

    /* #endregion */

    /* #region Pension choice handling: Deposit */

    const [pensionChoiceDepositDepositPercent, setPensionChoiceDepositDepositPercent] = useState(null);

    function pensionChoiceDepositDepositPercentText(value) {
        if (value) {
            return `${value} %`;
        }
    }

    const pensionChoiceDepositDepositPercentMarks = () => [
        {
            value: minDepositPercent,
            label: `${minDepositPercent}%`,
        },
        {
            value: penlyPlayState.pension.depositPerMonthInPercentCurrent,
            label: `${penlyPlayState.pension.depositPerMonthInPercentCurrent}% (nuværende)`,
        },
        {
            value: maxDepositPercent,
            label: `${maxDepositPercent}%`,
        }];

    const pensionChoiceDepositHandleDepositPercentChange = (event, value) => {
        setPensionChoiceDepositDepositPercent(value);
    };

    const pensionChoiceDepositHandleDepositPercentChangeBlur = async () => {
        const updatedState = { ...penlyPlayState };
        let runCalculation = false;

        // Reset other choice values
        updatedState.pension.pensionAgeChoice = PenlyPlayPensionAgeChoicesEnum.Unknown;
        updatedState.pension.pensionPaymentChoice = PenlyPlayPensionPaymentChoicesEnum.Unknown;

        if (pensionChoiceDepositDepositPercent > updatedState.pension.depositPerMonthInPercentCurrent) {
            if (updatedState.pension.pensionDepositChoice === PenlyPlayPensionDepositChoicesEnum.LowerDeposit) {
                updatedState.pension.pensionDepositGainChoice = PenlyPlayPensionDepositGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionDepositChoice = PenlyPlayPensionDepositChoicesEnum.HigherDeposit;
        } else {
            if (updatedState.pension.pensionDepositChoice === PenlyPlayPensionDepositChoicesEnum.HigherDeposit) {
                updatedState.pension.pensionDepositGainChoice = PenlyPlayPensionDepositGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionDepositChoice = PenlyPlayPensionDepositChoicesEnum.LowerDeposit;
        }

        updatedState.pension.depositPerMonthInPercentCalculated = pensionChoiceDepositDepositPercent;
        updatedState.pension.depositPerMonthCalculated = round((penlyPlayState.basic.salaryBeforeTax * pensionChoiceDepositDepositPercent) / 100);
        dispatch(updatePenlyPlayState(updatedState));

        if (runCalculation) {
            runUpdateCalculationBasedOnGainChoice(updatedState);
        }

        const titleElement = document.querySelector('#penlyPlayPensionGainTitle');

        if (titleElement) {
            const titleText = getPenlyPlayPensionGainStepTitle(updatedState);
            titleElement.innerHTML = titleText;
        }

        trackEvent({ category: 'PenlyPlayPension', action: 'PensionDepositChanged' });
    };

    /* #endregion */

    /* #region Pension choice handling: Payment */

    const [pensionChoicePaymentPaymentAmount, setPensionChoicePaymentPaymentAmount] = useState(null);

    function pensionChoicePaymentPaymentAmountText(value) {
        if (value) {
            return renderKr(value);
        }
    }

    function pensionChoicePaymentPaymentMaxAmount() {
        const roundValue = 10000;
        const paymentPerMonth = penlyPlayState.pension.paymentPerMonthCalculated ?? penlyPlayState.pension.paymentPerMonthCurrent;
        const maxAmount = Math.round(Math.max(penlyPlayState.basic.salaryBeforeTax, (paymentPerMonth + 10000)), 0);
        return Math.ceil(maxAmount / roundValue) * roundValue;
    }

    const pensionChoicePaymentPaymentAmountMarks = () => {
        const minPayment = 1000;

        return [
            {
                value: minPayment,
                label: renderKr(minPayment),
            },
            {
                value: penlyPlayState.pension.paymentPerMonthCurrent,
                label: `${renderKr(penlyPlayState.pension.paymentPerMonthCurrent)} (nuværende)`,
            },
            {
                value: pensionChoicePaymentPaymentMaxAmount(),
                label: renderKr(pensionChoicePaymentPaymentMaxAmount()),
            }];
    };

    const pensionChoicePaymentHandlePaymentAmountChange = (event, value) => {
        setPensionChoicePaymentPaymentAmount(value);
    };

    const pensionChoicePaymentHandlePaymentAmountChangeBlur = async () => {
        const updatedState = { ...penlyPlayState };
        let runCalculation = false;

        // Reset other choice values
        updatedState.pension.pensionAgeChoice = PenlyPlayPensionAgeChoicesEnum.Unknown;
        updatedState.pension.pensionDepositChoice = PenlyPlayPensionDepositChoicesEnum.Unknown;

        if (pensionChoicePaymentPaymentAmount > updatedState.pension.paymentPerMonthCurrent) {
            if (updatedState.pension.pensionPaymentChoice === PenlyPlayPensionPaymentChoicesEnum.LowerPayment) {
                updatedState.pension.pensionPaymentGainChoice = PenlyPlayPensionPaymentGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionPaymentChoice = PenlyPlayPensionPaymentChoicesEnum.HigherPayment;
        } else {
            if (updatedState.pension.pensionPaymentChoice === PenlyPlayPensionPaymentChoicesEnum.HigherPayment) {
                updatedState.pension.pensionPaymentGainChoice = PenlyPlayPensionPaymentGainChoicesEnum.Unknown;
                resetCalculatedValues(updatedState);
            } else {
                runCalculation = true;
            }
            updatedState.pension.pensionPaymentChoice = PenlyPlayPensionPaymentChoicesEnum.LowerPayment;
        }

        updatedState.pension.paymentPerMonthCalculated = pensionChoicePaymentPaymentAmount;
        dispatch(updatePenlyPlayState(updatedState));

        if (runCalculation) {
            runUpdateCalculationBasedOnGainChoice(updatedState);
        }

        const titleElement = document.querySelector('#penlyPlayPensionGainTitle');

        if (titleElement) {
            const titleText = getPenlyPlayPensionGainStepTitle(updatedState);
            titleElement.innerHTML = titleText;
        }

        trackEvent({ category: 'PenlyPlayPension', action: 'PensionPaymentChanged' });
    };

    /* #endregion */

    /* #region Pension gain choice handling */

    const onGainChoiceClick = async (event, choiceValue) => {
        const updatedState = { ...penlyPlayState };

        switch (penlyPlayState.pension.pensionChoice) {
            case PenlyPlayPensionChoicesEnum.Age:
                updatedState.pension.depositPerMonthCalculated = null;
                updatedState.pension.depositPerMonthInPercentCalculated = null;
                updatedState.pension.paymentPerMonthCalculated = null;
                updatedState.pension.pensionDevelopmentCalculated = null;
                updatedState.pension.savingsAtPensionCalculated = null;
                updatedState.pension.pensionAgeGainChoice = choiceValue;
                trackEvent({ category: 'PenlyPlayPension', action: 'PensionGainAgeChanged' });
                break;

            case PenlyPlayPensionChoicesEnum.Deposit:
                updatedState.pension.pensionAgeCalculated = null;
                updatedState.pension.paymentPerMonthCalculated = null;
                updatedState.pension.pensionDevelopmentCalculated = null;
                updatedState.pension.savingsAtPensionCalculated = null;
                updatedState.pension.pensionDepositGainChoice = choiceValue;
                trackEvent({ category: 'PenlyPlayPension', action: 'PensionGainDepositChanged' });
                break;

            case PenlyPlayPensionChoicesEnum.Payment:
                updatedState.pension.depositPerMonthCalculated = null;
                updatedState.pension.depositPerMonthInPercentCalculated = null;
                updatedState.pension.pensionAgeCalculated = null;
                updatedState.pension.pensionDevelopmentCalculated = null;
                updatedState.pension.savingsAtPensionCalculated = null;
                updatedState.pension.pensionPaymentGainChoice = choiceValue;
                trackEvent({ category: 'PenlyPlayPension', action: 'PensionGainPaymentChanged' });
                break;

            default:
                break;
        }

        dispatch(updatePenlyPlayState(updatedState));
        runUpdateCalculationBasedOnGainChoice(updatedState);
        event.preventDefault();
    };

    // React to gain choice and update result title
    useEffect(() => {
        const titleElement = document.querySelector('#penlyPlayPensionResultTitle');

        if (titleElement) {
            const titleText = getPenlyPlayPensionResultStepTitle(penlyPlayState);
            titleElement.innerHTML = titleText;
        };

    }, [penlyPlayState?.pension.pensionAgeGainChoice, penlyPlayState?.pension.pensionDepositGainChoice, penlyPlayState?.pension.pensionPaymentGainChoice]);

    /* #endregion */

    /* #region Helper methods */

    const checkHighlightValue = (pensionChoiceValue) => {

        switch (penlyPlayState.pension.pensionChoice) {
            case PenlyPlayPensionChoicesEnum.Age:
                switch (penlyPlayState.pension.pensionAgeGainChoice) {
                    case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit:
                    case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Deposit;

                    case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment:
                    case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Payment;
                    default:
                        break;
                }
                break;
            case PenlyPlayPensionChoicesEnum.Deposit:
                switch (penlyPlayState.pension.pensionDepositGainChoice) {
                    case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment:
                    case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Payment;

                    case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge:
                    case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Age;
                    default:
                        break;
                }
                break;
            case PenlyPlayPensionChoicesEnum.Payment:
                switch (penlyPlayState.pension.pensionPaymentGainChoice) {
                    case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge:
                    case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Age;

                    case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit:
                    case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit:
                        return pensionChoiceValue === PenlyPlayPensionChoicesEnum.Deposit;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        return false;

    };

    const getSavingDifference = () => {
        if (penlyPlayState.pension.savingsAtPensionCalculated && penlyPlayState.pension.savingsAtPensionCalculated !== penlyPlayState.pension.savingsAtPensionCurrent) {
            const difference = penlyPlayState.pension.savingsAtPensionCalculated - penlyPlayState.pension.savingsAtPensionCurrent;
            return difference > 0 ? ` (+${renderKr(difference)})` : ` (${renderKr(difference)})`;
        };
    };

    const resetCalculatedValues = (state) => {
        const updatedState = { ...state };
        updatedState.pension.pensionDevelopmentCalculated = null;
        updatedState.pension.savingsAtPensionCalculated = null;
        updatedState.pension.depositPerMonthCalculated = null;
        updatedState.pension.depositPerMonthInPercentCalculated = null;
        updatedState.pension.paymentPerMonthCalculated = null;
        updatedState.pension.pensionAgeCalculated = null;
    };

    const runUpdateCalculationBasedOnGainChoice = (updatedState) => {
        switch (penlyPlayState.pension.pensionChoice) {
            case PenlyPlayPensionChoicesEnum.Age:

                switch (updatedState.pension.pensionAgeGainChoice) {
                    case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit:
                    case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit:
                        updateDepositCalculation();
                        break;
                    case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment:
                    case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment:
                        updatePaymentCalculation();
                        break;
                    default:
                        break;
                }
                break;

            case PenlyPlayPensionChoicesEnum.Deposit:
                switch (updatedState.pension.pensionDepositGainChoice) {
                    case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge:
                    case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge:
                        updateAgeCalculation();
                        break;
                    case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment:
                    case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment:
                        updatePaymentCalculation();
                        break;
                    default:
                        break;
                }
                break;

            case PenlyPlayPensionChoicesEnum.Payment:
                switch (updatedState.pension.pensionPaymentGainChoice) {
                    case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit:
                    case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit:
                        updateDepositCalculation();
                        break;
                    case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge:
                    case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge:
                        updateAgeCalculation();
                        break;
                    default:
                        break;
                }
                break;

            default:
                break;

        }
    };

    const toggleSummaryBox = () => {
        const container = document.querySelector('.penlyPlayPensionSummaryContainer');
        container.classList.toggle('toggled');
    };

    /* #endregion */

    /* #region Calculation methods */

    // Calculate using current values. This is called whenever a basic/input value is changed
    const updateCurrentCalculation = async (forceCalculation = false) => {
        if (forceCalculation === true || (initialized && baseValueChanged)) {
            log(`updateBaseCalculation(${forceCalculation})`);
            setIsCalculating(true);
            const currentCalculation = await getCurrentCalculation(penlyPlayState);

            if (currentCalculation) {
                const updatedState = { ...penlyPlayState };
                updatedState.pension.pensionDevelopmentCurrent = currentCalculation.pensionDevelopment;
                updatedState.pension.savingsAtPensionCurrent = currentCalculation.savingsAtPension;
                updatedState.pension.paymentPerMonthCurrent = currentCalculation.paymentPerMonth;
                updatedState.pension.calculationValues = currentCalculation.calculationValues;
                dispatch(updatePenlyPlayState(updatedState));

                if (!initialized) {
                    setInitialized(true);
                }

                setBaseValueChanged(false);
                setDoFlash(true);
            }
            setIsCalculating(false);
        }
    };

    // Calculate payment
    const updatePaymentCalculation = async () => {
        log('updatePaymentCalculation()');
        setIsCalculating(true);
        const paymentCalculation = await getPaymentCalculation(penlyPlayState);

        if (paymentCalculation) {
            const updatedState = { ...penlyPlayState };

            // Update common values
            updatedState.pension.pensionDevelopmentCalculated = paymentCalculation.pensionDevelopment;
            updatedState.pension.savingsAtPensionCalculated = paymentCalculation.savingsAtPension;

            // Update payment values
            updatedState.pension.paymentPerMonthCalculated = paymentCalculation.paymentPerMonth;

            dispatch(updatePenlyPlayState(updatedState));
            setDoFlash(true);
        }
        setIsCalculating(false);
    };

    // Calculate deposit
    const updateDepositCalculation = async () => {
        log('updateDepositCalculation()');
        setIsCalculating(true);
        const depositCalculation = await getDepositCalculation(penlyPlayState);

        if (depositCalculation) {
            const updatedState = { ...penlyPlayState };

            // Update common values
            updatedState.pension.pensionDevelopmentCalculated = depositCalculation.pensionDevelopment;
            updatedState.pension.savingsAtPensionCalculated = depositCalculation.savingsAtPension;

            // Update deposit values
            updatedState.pension.depositPerMonthCalculated = depositCalculation.depositPerMonth;
            updatedState.pension.depositPerMonthInPercentCalculated = parseInt(Math.ceil((updatedState.pension.depositPerMonthCalculated * 100) / updatedState.basic.salaryBeforeTax), 0);

            dispatch(updatePenlyPlayState(updatedState));
            setDoFlash(true);
        }
        setIsCalculating(false);
    };

    // Calculate age
    const updateAgeCalculation = async () => {
        log('updateAgeCalculation()');
        setIsCalculating(true);
        const ageCalculation = await getAgeCalculation(penlyPlayState);

        if (ageCalculation) {
            const updatedState = { ...penlyPlayState };

            // Update common values
            updatedState.pension.pensionDevelopmentCalculated = ageCalculation.pensionDevelopment;
            updatedState.pension.savingsAtPensionCalculated = ageCalculation.savingsAtPension;

            // Update age values
            updatedState.pension.pensionAgeCalculated = ageCalculation.pensionAge;

            dispatch(updatePenlyPlayState(updatedState));
            setDoFlash(true);
        }
        setIsCalculating(false);
    };

    /* #endregion */

    /* #region Result methods */

    const getDepositPerMonth = (percent) => (penlyPlayState.basic.salaryBeforeTax * percent) / 100;

    /* #endregion */

    // Initialize at load
    useEffect(() => {

        if (!penlyPlayState || !penlyPlayState.initialized) {
            log('useEffect() - initialize');

            let playState = { };

            if (publicRuntimeConfig.env === 'dev') {
                playState = {
                    basic: { ...initialPlayState.basic, ...userPlayState.basic },
                    pension: { ...initialPlayState.pension, ...userPlayState.pension },
                };
            } else {
                playState = {
                    basic: { ...initialPlayState.basic },
                    pension: { ...initialPlayState.pension },
                };
            }

            const initialPenlyPlayState = getInitialPenlyPlayState(playState);
            initialPenlyPlayState.initialized = true;
            dispatch(resetPenlyPlayState(initialPenlyPlayState));

            trackEvent({ category: 'PenlyPlayPension', action: 'Initialized' });
        }
    }, []);

    // React to required input state change. This is also used to do the first/initial API call.
    useEffect(() => {
        if (penlyPlayState?.hasRequiredPensionInput) {
            log('useEffect() - hasRequiredPensionInput()');
            ;(async () => {
                await updateCurrentCalculation(true);
            })();
        }
    }, [penlyPlayState?.hasRequiredPensionInput]);

    const toggleCalculationInfo = () => {
        setShowCalculationInfo(!showCalculationInfo);
    };

    return (
        <article className="penlyPlayPension twocol6040" { ...props } >
            <section className="stepper vertical penlyPlayPensionStepper">
                <div className="penlyPlayPensionStepAboutYou" id="stepperStep1">
                    <h3 data-index="1">Lidt om dig</h3>
                    <div>
                        <div className="formgroups">
                            <div className="formgroup">
                                <TextField
                                    id="age"
                                    label="Alder"
                                    variant="filled"
                                    value={ age }
                                    InputProps={{
                                        inputComponent: AgeFormat,
                                    }}
                                    onBlur={ handleInputBlur }
                                    onChange={ (e) => setAge(e.target.value) }
                                    onKeyDown={ handleKeyDown }
                                />
                            </div><div className="formgroup">
                                <TextField
                                    id="salary"
                                    label="Løn før skat per måned"
                                    variant="filled"
                                    value={ salaryBeforeTax }
                                    InputProps={{
                                        inputComponent: CurrencyFormat,
                                    }}
                                    onBlur={ handleInputBlur }
                                    onChange={ (e) => setSalaryBeforeTax(e.target.value) }
                                    onKeyDown={ handleKeyDown }
                                />
                            </div>
                        </div>
                        <div className="formgroups">
                            <div className="formgroup">
                                <TextField
                                    id="pensionSaved"
                                    label="Pension opsparet til dato"
                                    variant="filled"
                                    value={ pensionSavedToDate }
                                    InputProps={{
                                        inputComponent: CurrencyFormat,
                                    }}
                                    onBlur={ handleInputBlur }
                                    onChange={ (e) => setPensionSavedToDate(e.target.value) }
                                    onKeyDown={ handleKeyDown }
                                />
                            </div>
                        </div>
                        <div className="formgroups">
                            <div className="formgroup">
                                <Slider
                                    label="Pension indbetalt af løn per måned i %"
                                    step={ 1 }
                                    min={ minDepositPercent }
                                    max={ maxDepositPercent }
                                    value={ depositPerMonthInPercentCurrent }
                                    getAriaValueText={ percentText }
                                    valueLabelFormat={ percentText }
                                    marks={ depositPerMonthMarks }
                                    onChangeCommitted={ handleDepositPerMonthInPercentCurrentInputBlur }
                                    onChange={ (e, value) => setDepositPerMonthInPercentCurrent(value) }
                                />
                            </div>
                        </div>

                        <a onClick={ () => toggleCalculationInfo() } className="infoAnchor">Se forudsætninger for beregningen</a>

                        { showCalculationInfo &&
                        <div className="modalOverlay" onClick={ () => toggleCalculationInfo() }>
                            <div className="infoDialog calculationInfoDialog">
                                <div>
                                    <a onClick={ () => toggleCalculationInfo() }><div></div></a>
                                    <div className="inner">
                                        <h4>Hvordan har vi beregnet det?</h4>
                                        <ul>
                                            <li>Der regnes med en inflationsrate på 1,5% gennem hele beregningsperioden.</li>
                                            <li>Afkast på pension er sat til 5% under opsparing og til 3% under udbetaling.</li>
                                            <li>Alle tal vises i nutidskroner, dvs. de fremtidige udbetalinger bliver nedskrevet med inflationen til den værdi pengene har i dag. Det betyder, at købekraften af det viste beløb om 30 år svarer til det samme som i dag. F.eks. vil en årlig pensionsudbetaling på 46.200 kr. om 30 år vises i nutidsværdi på 30.000 kr. pga. den årlige inflationsrate på 1,5 %.</li>
                                            <li>Vi forudsætter, at udbetalingsperioden starter fra det øjeblik man ønsker at gå på pension og slutter 20 år efter folkepensionsalder.</li>
                                            <li>Tidligere udbetaling end 3 år før folkepensionsalder kræver, at ordningen kan blive udbetalt.</li>
                                            <li>Den estimerede pensionsalder er folkepensionsalderen beregnet ud fra din alder.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                </div>
                { penlyPlayState?.hasRequiredPensionInput &&
                    <>
                        { (penlyPlayState.pension.pensionDevelopmentCalculated || penlyPlayState.pension.pensionDevelopmentCurrent) &&
                            <>
                                <div id="stepperStep2" className="developmentStep">
                                    <h3 data-index="2">Pensionsudvikling</h3>
                                    <div>
                                        <p>Her er en estimeret udvikling for din pension - før og efter du går på pension.</p>
                                        <br />
                                        <PenlyPlayDevelopmentGraph
                                            data={
                                                {
                                                    values: penlyPlayState.pension.pensionDevelopmentCalculated ?? penlyPlayState.pension.pensionDevelopmentCurrent,
                                                    pensionAgeEstimated: penlyPlayState.pension.pensionAgeCalculated !== null && penlyPlayState.pension.pensionDevelopmentCalculated ?
                                                        Math.floor(penlyPlayState.pension.pensionAgeCalculated) :
                                                        Math.floor(penlyPlayState.pension.pensionAgeCurrent),
                                                    pensionSavedToDate: penlyPlayState.pension.pensionSavedToDate,
                                                }
                                            }
                                        ></PenlyPlayDevelopmentGraph>
                                    </div>
                                </div>
                                <div id="stepperStep3">
                                    <h3 data-index="3">Hvad ønsker du at ændre ved din pension?</h3>
                                    <div>
                                        <div className='buttonGroup'>
                                            <Button active={ penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age } variant='outline' noshadow={ true }
                                                onClick={ (e) => onPensionChoiceClick(e, PenlyPlayPensionChoicesEnum.Age) }>Pensionsalder</Button>
                                            <Button active={ penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit } variant='outline' noshadow={ true }
                                                onClick={ (e) => onPensionChoiceClick(e, PenlyPlayPensionChoicesEnum.Deposit) }>Indbetaling</Button>
                                            <Button active={ penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment } variant='outline' noshadow={ true }
                                                onClick={ (e) => onPensionChoiceClick(e, PenlyPlayPensionChoicesEnum.Payment) }>Udbetaling</Button>
                                        </div>
                                        <Spacer size="small" />

                                        <div style={{ width: '100%' }}>
                                            { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age &&
                                            <>
                                                <p>Træk og vælg din ønskede pensionsalder.</p>
                                                <Slider
                                                    id="penlyPlayPensionPensionAgeSlider"
                                                    step={ 1 }
                                                    min={ 55 }
                                                    max={ 85 }
                                                    value={ pensionChoiceAgePensionAge }
                                                    getAriaValueText={ pensionChoiceAgePensionAgeText }
                                                    valueLabelFormat={ pensionChoiceAgePensionAgeText }
                                                    marks={ getPensionChoiceAgePensionAgeMarks() }
                                                    onChange={ pensionChoiceAgeHandlePensionAgeChange }
                                                    onChangeCommitted={ pensionChoiceAgeHandlePensionAgeChangeBlur }
                                                />
                                            </>
                                            }

                                            { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit &&
                                            <div>
                                                <p>Træk og vælg din ønskede indbetalingsprocent.</p>
                                                <Slider
                                                    id="penlyPlayPensionDepositPercentSlider"
                                                    step={ 1 }
                                                    min={ minDepositPercent }
                                                    max={ maxDepositPercent }
                                                    value={ pensionChoiceDepositDepositPercent }
                                                    getAriaValueText={ pensionChoiceDepositDepositPercentText }
                                                    valueLabelFormat={ pensionChoiceDepositDepositPercentText }
                                                    marks={ pensionChoiceDepositDepositPercentMarks() }
                                                    onChange={ pensionChoiceDepositHandleDepositPercentChange }
                                                    onChangeCommitted={ pensionChoiceDepositHandleDepositPercentChangeBlur }
                                                />
                                            </div>
                                            }

                                            { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment &&
                                            <div>
                                                <p>Træk og vælg din ønskede månedlige udbetaling.</p>
                                                <Slider
                                                    id="penlyPlayPensionPaymentSlider"
                                                    step={ 1000 }
                                                    min={ 1000 }
                                                    max={ pensionChoicePaymentPaymentMaxAmount() }
                                                    value={ pensionChoicePaymentPaymentAmount }
                                                    getAriaValueText={ pensionChoicePaymentPaymentAmountText }
                                                    valueLabelFormat={ pensionChoicePaymentPaymentAmountText }
                                                    marks={ pensionChoicePaymentPaymentAmountMarks() }
                                                    onChange={ pensionChoicePaymentHandlePaymentAmountChange }
                                                    onChangeCommitted={ pensionChoicePaymentHandlePaymentAmountChangeBlur }
                                                />
                                            </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                { penlyPlayState.pension.pensionChoice && (
                                    (penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age && penlyPlayState.pension.pensionAgeChoice) ||
                                    (penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit && penlyPlayState.pension.pensionDepositChoice) ||
                                    (penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment && penlyPlayState.pension.pensionPaymentChoice)
                                ) &&
                                    <>
                                        <div id="stepperStep4">
                                            <h3 data-index="4" id="penlyPlayPensionGainTitle">Trin 4</h3>
                                            <div className="buttonGroup">
                                                { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age &&
                                                <>
                                                    { penlyPlayState.pension.pensionAgeChoice === PenlyPlayPensionAgeChoicesEnum.LowerAge &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit } variant='outline' noshadow={ true }
                                                            onClick={ (e) => onGainChoiceClick(e, PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit) }>Øget indbetaling</Button>
                                                        <Button active={ penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment } variant='outline' noshadow={ true }
                                                            onClick={ (e) => onGainChoiceClick(e, PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment) }>Mindre udbetalt</Button>
                                                    </>
                                                    }
                                                    { penlyPlayState.pension.pensionAgeChoice === PenlyPlayPensionAgeChoicesEnum.HigherAge &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit } variant='outline' noshadow={ true }
                                                            onClick={ (e) => onGainChoiceClick(e, PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit) }>Mindre indbetaling</Button>
                                                        <Button active={ penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment } variant='outline' noshadow={ true }
                                                            onClick={ (e) => onGainChoiceClick(e, PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment) }>Øget udbetalt</Button>
                                                    </>
                                                    }
                                                </>
                                                }

                                                { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit &&
                                                <>
                                                    { penlyPlayState.pension.pensionDepositChoice === PenlyPlayPensionDepositChoicesEnum.LowerDeposit &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment) }>Mindre udbetalt</Button>
                                                        <Button active={ penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge) }>Senere pension</Button>
                                                    </>
                                                    }
                                                    { penlyPlayState.pension.pensionDepositChoice === PenlyPlayPensionDepositChoicesEnum.HigherDeposit &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment) }>Mere udbetalt</Button>
                                                        <Button active={ penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge) }>Tidligere pension</Button>
                                                    </>
                                                    }
                                                </>
                                                }

                                                { penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment &&
                                                <>
                                                    { penlyPlayState.pension.pensionPaymentChoice === PenlyPlayPensionPaymentChoicesEnum.LowerPayment &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit) }>Indbetale mindre</Button>
                                                        <Button active={ penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge) }>Tidligere pension</Button>
                                                    </>
                                                    }
                                                    { penlyPlayState.pension.pensionPaymentChoice === PenlyPlayPensionPaymentChoicesEnum.HigherPayment &&
                                                    <>
                                                        <Button active={ penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit) }>Højere indbetaling</Button>
                                                        <Button active={ penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge } variant='outline' noshadow={ true } onClick={ (e) =>
                                                            onGainChoiceClick(e, PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge) }>Senere pension</Button>
                                                    </>
                                                    }
                                                </>
                                                }
                                            </div>
                                        </div>
                                        { penlyPlayState.pension.pensionChoice &&
                                        (
                                            penlyPlayState.pension.pensionAgeGainChoice !== PenlyPlayPensionAgeGainChoicesEnum.Unknown ||
                                            penlyPlayState.pension.pensionDepositGainChoice !== PenlyPlayPensionDepositGainChoicesEnum.Unknown ||
                                            penlyPlayState.pension.pensionPaymentGainChoice !== PenlyPlayPensionPaymentGainChoicesEnum.Unknown
                                        ) &&
                                        <>
                                            <div id="stepperStep5">
                                                <h3 data-index="5" id="penlyPlayPensionResultTitle">Trin 5</h3>
                                                <div className="penlyPlayPensionResult">
                                                    {
                                                        (
                                                            (
                                                                penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age &&
                                                            (
                                                                penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit ||
                                                                penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit
                                                            )
                                                            )
                                                        ||
                                                        (
                                                            penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment &&
                                                            (
                                                                penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit ||
                                                                penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit
                                                            )
                                                        )
                                                        )
                                                     &&
                                                     <>
                                                         { penlyPlayState.pension.depositPerMonthCalculated === penlyPlayState.pension.depositPerMonthCurrent ?
                                                             <>
                                                                 <p>
                                                                     Din pensions indbetaling forbliver{ ' ' }
                                                                     { renderKr(getDepositPerMonth(penlyPlayState.pension.depositPerMonthInPercentCalculated)) }{ ' ' }
                                                                     om måneden.
                                                                 </p>
                                                                 <div>
                                                                     <div data-text="Ingen ændring" className="valueFlash">
                                                                         { renderKr(penlyPlayState.pension.depositPerMonthCalculated) }
                                                                     </div>
                                                                 </div>
                                                             </>
                                                             :
                                                             <>
                                                                 {
                                                                     (
                                                                         penlyPlayState.pension.depositPerMonthCalculated <= 0 &&
                                                                         (penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit ||
                                                                         penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit)
                                                                     ) ?
                                                                         <>
                                                                             <p>Du behøver ikke indbetale mere til pension med en ønsket udbetaling på{ ' ' }
                                                                                 <strong>{ renderKr(penlyPlayState.pension.paymentPerMonthCalculated) }</strong>
                                                                             </p>
                                                                             <div>
                                                                                 <div data-text="Før">
                                                                                     { penlyPlayState.pension.depositPerMonthInPercentCurrent }% ({ renderKr(penlyPlayState.pension.depositPerMonthCurrent) })
                                                                                 </div>
                                                                                 <div data-text="Efter" className="valueFlash">
                                                                                     0% ({ renderKr(0) })
                                                                                 </div>
                                                                             </div>
                                                                         </>
                                                                         :
                                                                         <>
                                                                             <p>Din pensionsindbetaling{ ' ' }
                                                                                 { penlyPlayState.pension.depositPerMonthCalculated > penlyPlayState.pension.depositPerMonthCurrent ?
                                                                                     <>
                                                                                         stiger
                                                                                     </>
                                                                                     :
                                                                                     <>
                                                                                         falder
                                                                                     </>
                                                                                 }
                                                                                 { ' ' }fra
                                                                                 { ' ' }<strong>{ renderKr(penlyPlayState.pension.depositPerMonthCurrent) }</strong>
                                                                                 { ' ' }til
                                                                                 { ' ' }<strong>{ renderKr(penlyPlayState.pension.depositPerMonthCalculated) }</strong>
                                                                                 { ' ' }om måneden.
                                                                             </p>
                                                                             <div>
                                                                                 <div data-text="Før">
                                                                                     { penlyPlayState.pension.depositPerMonthInPercentCurrent }% ({ renderKr(penlyPlayState.pension.depositPerMonthCurrent) })
                                                                                 </div>
                                                                                 <div data-text="Efter" className="valueFlash">
                                                                                     { penlyPlayState.pension.depositPerMonthInPercentCalculated }% ({ renderKr(penlyPlayState.pension.depositPerMonthCalculated) })
                                                                                 </div>
                                                                             </div>
                                                                         </>
                                                                 }
                                                             </>
                                                         }
                                                     </>
                                                    }

                                                    {
                                                        (
                                                            (
                                                                penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Age &&
                                                            (
                                                                penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment ||
                                                                penlyPlayState.pension.pensionAgeGainChoice === PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment
                                                            )
                                                            )
                                                        ||
                                                        (
                                                            penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit &&
                                                            (
                                                                penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment ||
                                                                penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment
                                                            )
                                                        )
                                                        )
                                                     &&
                                                     <>
                                                         { penlyPlayState.pension.paymentPerMonthCalculated === penlyPlayState.pension.paymentPerMonthCurrent ?
                                                             <>
                                                                 <p>
                                                                     Din pensionsudbetaling forbliver{ ' ' }
                                                                     { renderKr(penlyPlayState.pension.paymentPerMonthCalculated) }{ ' ' }
                                                                     om måneden.
                                                                 </p>
                                                                 <div>
                                                                     <div data-text="Ingen ændring" className="valueFlash">
                                                                         { renderKr(penlyPlayState.pension.paymentPerMonthCalculated) }
                                                                     </div>
                                                                 </div>
                                                             </>
                                                             :
                                                             <>
                                                                 <p>Din pensions udbetaling{ ' ' }
                                                                     { penlyPlayState.pension.paymentPerMonthCalculated > penlyPlayState.pension.paymentPerMonthCurrent ?
                                                                         <>
                                                                             stiger
                                                                         </>
                                                                         :
                                                                         <>
                                                                             falder
                                                                         </>
                                                                     }
                                                                     { ' ' }fra
                                                                     { ' ' }<strong>{ renderKr(penlyPlayState.pension.paymentPerMonthCurrent) }</strong>
                                                                     { ' ' }til
                                                                     { ' ' }<strong>{ renderKr(penlyPlayState.pension.paymentPerMonthCalculated) }</strong>
                                                                     { ' ' }om måneden.
                                                                 </p>
                                                                 <div>
                                                                     <div data-text="Før">
                                                                         { renderKr(penlyPlayState.pension.paymentPerMonthCurrent) }
                                                                     </div>
                                                                     <div data-text="Efter" className="valueFlash">
                                                                         { renderKr(penlyPlayState.pension.paymentPerMonthCalculated) }
                                                                     </div>
                                                                 </div>
                                                             </>
                                                         }
                                                     </>
                                                    }

                                                    {
                                                        (
                                                            (
                                                                penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Payment &&
                                                            (
                                                                penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge ||
                                                                penlyPlayState.pension.pensionPaymentGainChoice === PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge
                                                            )
                                                            )
                                                        ||
                                                        (
                                                            penlyPlayState.pension.pensionChoice === PenlyPlayPensionChoicesEnum.Deposit &&
                                                            (
                                                                penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge ||
                                                                penlyPlayState.pension.pensionDepositGainChoice === PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge
                                                            )
                                                        )
                                                        )
                                                     &&
                                                     <>
                                                         { penlyPlayState.pension.pensionAgeCalculated === penlyPlayState.pension.pensionAgeCurrent ?
                                                             <>
                                                                 <p>
                                                                     Din pensionsalder forbliver{ ' ' }
                                                                     { penlyPlayState.pension.pensionAgeCalculated }{ ' ' }
                                                                     år.
                                                                 </p>
                                                                 <div>
                                                                     <div data-text="Ingen ændring" className="valueFlash">
                                                                         { penlyPlayState.pension.pensionAgeCalculated } år
                                                                     </div>
                                                                 </div>
                                                             </>
                                                             :
                                                             <>
                                                                 <p>Din pensionsalder { ' ' }
                                                                     { penlyPlayState.pension.pensionAgeCalculated > penlyPlayState.pension.pensionAgeCurrent ?
                                                                         <>
                                                                             stiger
                                                                         </>
                                                                         :
                                                                         <>
                                                                             falder
                                                                         </>
                                                                     }
                                                                     { ' ' }fra
                                                                     { ' ' }<strong>{ penlyPlayState.pension.pensionAgeCurrent }</strong>
                                                                     { ' ' }til
                                                                     { ' ' }<strong>{ penlyPlayState.pension.pensionAgeCalculated }</strong>
                                                                     { ' ' }år.
                                                                 </p>
                                                                 <div>
                                                                     <div data-text="Før">
                                                                         { penlyPlayState.pension.pensionAgeCurrent } år
                                                                     </div>
                                                                     <div data-text="Efter" className="valueFlash">
                                                                         { penlyPlayState.pension.pensionAgeCalculated } år
                                                                     </div>
                                                                 </div>
                                                             </>
                                                         }
                                                     </>
                                                    }
                                                </div>
                                            </div>

                                            <div id="stepperStep6" className="lastStep">
                                                <h2 data-index="6">Kom i gang med dit pensionstjek på 5 minutter</h2>
                                                <div>
                                                    <p className="large">Prøv vores gratis pensionsberegner, og følg med i om din pension vokser, som den skal.</p>
                                                    <br />
                                                    { !authenticated ?
                                                        <>
                                                            <Button size="large" color="secondary" onClick={ () => router.push(Routes.PensionCheck.Start) }>
                                                                Start PensionTjek
                                                            </Button>
                                                        </>
                                                        :
                                                        <>
                                                            <Button size="large" color="secondary" onClick={ () => router.push(Routes.MyPenly.Pension.Dashboard ) }>
                                                                Ja tak, lad mig prøve
                                                            </Button>
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                        </>
                                        }
                                    </>
                                }
                                <Spacer size="large" />
                            </>
                        }
                    </>
                }
            </section>
            <section>
                { penlyPlayState && penlyPlayState?.hasRequiredPensionInput ?
                    <div className="penlyPlayPensionSummaryContainer">
                        <div className={ isCalculating ? 'penlyPlayPensionSummaryBox penlyPlayCalculating' : 'penlyPlayPensionSummaryBox' }>
                            <h4 onClick={ () => toggleSummaryBox() } data-showtext="Vis resultat" data-hidetext="Skjul">Din pension</h4>
                            <div>
                                <div className={ penlyPlayState.pension.depositPerMonthCalculated && checkHighlightValue(PenlyPlayPensionChoicesEnum.Deposit) ? 'highlight valueFlash' : '' }>
                                    { penlyPlayState.pension.depositPerMonthCalculated && penlyPlayState.pension.depositPerMonthCalculated <= 0 ?
                                        <>
                                            <div>0 % af løn</div>
                                            <div className="small">Indbetalt til pension om måneden</div>
                                        </>
                                        :
                                        <>
                                            <div>
                                                { penlyPlayState.pension.depositPerMonthInPercentCalculated ?? penlyPlayState.pension.depositPerMonthInPercentCurrent }% af løn
                                                ({ renderKr(penlyPlayState.pension.depositPerMonthCalculated ?? penlyPlayState.pension.depositPerMonthCurrent) })</div>
                                            <div className="small">Indbetalt til pension om måneden</div>
                                        </>
                                    }
                                </div>
                                {
                                    (
                                        penlyPlayState.pension.pensionDevelopmentCalculated &&
                                        penlyPlayState.pension.pensionAgeCalculated &&
                                        penlyPlayState.pension.pensionAgeCalculated > 0
                                    ) ?
                                        <div className={ penlyPlayState.pension.pensionAgeCalculated && checkHighlightValue(PenlyPlayPensionChoicesEnum.Age) ? 'highlight valueFlash' : '' }>
                                            <div>{ penlyPlayState.pension.pensionAgeCalculated } år</div>
                                            <div className="small">Pensionsalder <span></span></div>
                                        </div>
                                        :
                                        <div>
                                            <div>{ penlyPlayState.pension.pensionAgeCurrent } år</div>
                                            <div className="small">Pensionsalder <span>(Estimeret)</span></div>
                                        </div>
                                }
                                <div className={ penlyPlayState.pension.savingsAtPensionCalculated ? 'highlightsecondary' : '' }>
                                    <div>{ renderKr(penlyPlayState.pension.savingsAtPensionCalculated ?? penlyPlayState.pension.savingsAtPensionCurrent) }</div>
                                    <div className="small">
                                        Opsparing ved pension{ getSavingDifference() }
                                    </div>
                                </div>
                                <div className={ penlyPlayState.pension.paymentPerMonthCalculated && checkHighlightValue(PenlyPlayPensionChoicesEnum.Payment) ? 'highlight valueFlash' : '' }>
                                    <div>{ renderKr(penlyPlayState.pension.paymentPerMonthCalculated ?? penlyPlayState.pension.paymentPerMonthCurrent) }</div>
                                    <div className="small">Udbetalt månedligt som pensionist</div>
                                </div>
                            </div>
                            <div>
                                { !authenticated ?
                                    <>
                                        <Button color="secondary" onClick={ () => router.push(Routes.PensionCheck.Start) }>Tag et PensionsTjek</Button>
                                        <p>Det koster kun 5 minutter af din tid.</p>
                                        <a onClick={ login }>Har du allerede en profil, klik her</a>
                                    </>
                                    :
                                    <>
                                        <Button color="secondary" onClick={ () => router.push(Routes.MyPenly.Pension.Dashboard) }>Tag et nyt PensionsTjek</Button>
                                        <p>Upload en ny pensionsrapport,</p>
                                        <p>,det tager kun 5 minutter.</p>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                    :
                    <div className="penlyPlayPensionSummaryContainer">
                        <div className="penlyPlayPensionSummaryBox inputMessage">
                            <h4 onClick={ () => toggleSummaryBox() } data-showtext="Vis" data-hidetext="Skjul">Kom igang</h4>
                            <div>
                                <p>
                                    Indtast din alder, løn og pension for at se din nuværende pensionsudvikling.
                                </p>
                            </div>
                            <div className="arrow"></div>
                        </div>
                    </div>
                }
            </section>
        </article>
    );
};

PenlyPlayPension.propTypes = {};
PenlyPlayPension.defaultProps = {};

export default PenlyPlayPension;
